import React, { useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Radar, LineChart, Users, Bookmark, Bell,
  BookOpen, ShieldCheck, LogOut, User as UserIcon, Sparkles,
  Clock, Grid3x3, Archive, MessagesSquare, CalendarDays,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import DataFreshness from "@/components/DataFreshness";
import TrustBanner from "@/components/TrustBanner";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-overview" },
  { to: "/app/today", label: "Today", icon: CalendarDays, testid: "nav-today" },
  { to: "/app/signals", label: "Signals", icon: Radar, testid: "nav-signals" },
  { to: "/app/timeline", label: "Timeline", icon: Clock, testid: "nav-timeline" },
  { to: "/app/markets", label: "Markets", icon: LineChart, testid: "nav-markets" },
  { to: "/app/traders", label: "Traders", icon: Users, testid: "nav-traders" },
  { to: "/app/overlap", label: "Portfolio Overlap", icon: Grid3x3, testid: "nav-overlap" },
  { to: "/app/watchlists", label: "Watchlists", icon: Bookmark, testid: "nav-watchlists" },
  { to: "/app/history", label: "History", icon: Archive, testid: "nav-history" },
  { to: "/app/chat", label: "Chat", icon: MessagesSquare, testid: "nav-chat" },
  { to: "/app/alerts", label: "Alerts", icon: Bell, testid: "nav-alerts" },
  { to: "/app/account", label: "Account", icon: UserIcon, testid: "nav-account" },
  { to: "/app/methodology", label: "Methodology", icon: BookOpen, testid: "nav-methodology" },
];

const ADMIN_NAV = [
  { to: "/app/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const wakeFired = useRef(false);

  // Wake sync — the moment the authenticated shell mounts, ask the backend to
  // refresh live prices if data is stale (>5 min). Fires once per session-load;
  // backend also de-bounces to 60s across tabs.
  useEffect(() => {
    if (wakeFired.current || !user) return;
    wakeFired.current = true;
    api.post("/dashboard/wake").catch(() => {});
  }, [user]);

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-56 border-r border-zinc-800 flex-col z-30 bg-[#0b0b0f]">
        <div className="h-14 flex items-center gap-2 px-5 border-b border-zinc-800">
          <div className="w-6 h-6 rounded-sm bg-zinc-100 text-zinc-950 grid place-items-center">
            <Sparkles size={13} />
          </div>
          <span className="font-heading font-bold tracking-tight text-[15px]">MarketTrace</span>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-zinc-800/70 text-zinc-50"
                    : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
                }`
              }
            >
              <n.icon size={15} strokeWidth={1.8} />
              {n.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <div className="mt-4 mb-1 px-3 text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                Admin
              </div>
              {ADMIN_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                      isActive
                        ? "bg-zinc-800/70 text-zinc-50"
                        : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
                    }`
                  }
                >
                  <n.icon size={15} strokeWidth={1.8} />
                  {n.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="border-t border-zinc-800 p-3 space-y-2">
          <div className="flex items-center gap-2 px-2 text-xs text-zinc-500">
            <UserIcon size={13} />
            <span className="truncate" data-testid="user-email">{user?.email}</span>
          </div>
          <button
            onClick={doLogout}
            data-testid="btn-logout"
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 rounded-md transition-colors"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 md:ml-56 min-h-screen flex flex-col">
        {/* Top bar */}
        <TrustBanner />
        <header className="blur-surface sticky top-0 z-20 border-b border-zinc-800 min-h-14 flex items-center px-3 md:px-6 gap-2 sm:gap-3">
          <div className="md:hidden font-heading font-bold text-sm">MarketTrace</div>
          <div className="flex-1" />
          <DataFreshness />
          <NotificationBell />
        </header>

        <main className="flex-1 px-4 md:px-8 py-6">
          <Outlet />
        </main>

        {/* Mobile nav bar */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 blur-surface border-t border-zinc-800 grid grid-cols-5 pb-[env(safe-area-inset-bottom)]">
          {NAV.slice(0, 5).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-2 text-[10px] ${
                  isActive ? "text-zinc-100" : "text-zinc-500"
                }`
              }
            >
              <n.icon size={16} />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
